import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtTL } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Tag, Loader2, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle, Search, Send } from "lucide-react";

function Stat({ label, value, cls, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-bold ${cls || "text-slate-800"}`}>{value}</p>
    </div>
  );
}

export default function Esl() {
  const { can } = useAuth();
  const canManage = can("esl.manage");
  const { locations: locs } = useActiveLocation();
  const [labels, setLabels] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, in_sync: 0, out_of_sync: 0, never_pushed: 0 });
  const [locFilter, setLocFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (locFilter) params.location_id = locFilter;
    if (q) params.q = q;
    Promise.all([
      api.get("/esl/labels", { params }),
      api.get("/esl/summary", { params: locFilter ? { location_id: locFilter } : {} }),
    ]).then(([l, s]) => { setLabels(l.data || []); setSummary(s.data || {}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [locFilter, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pushOne = async (l) => {
    setBusy(l.id);
    try { const { data } = await api.post(`/esl/labels/${l.id}/push`); toast.success(`Gönderildi: ${fmtTL(data.price)}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const pushAll = async () => {
    setBusy("all");
    try {
      const { data } = await api.post("/esl/push-all", null, { params: locFilter ? { location_id: locFilter } : {} });
      toast.success(`${data.pushed} etikete fiyat gönderildi (simüle)`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const toggleStatus = async (l) => {
    try { await api.put(`/esl/labels/${l.id}`, { status: l.status === "active" ? "inactive" : "active" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (l) => {
    if (!window.confirm(`${l.label_code} etiketi silinsin mi?`)) return;
    try { await api.delete(`/esl/labels/${l.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [cfg, setCfg] = useState(null);
  const [cfgSaving, setCfgSaving] = useState(false);
  useEffect(() => { api.get("/esl/config").then((r) => setCfg({ ...r.data, api_key: "" })).catch(() => {}); }, []);
  const saveCfg = async () => {
    setCfgSaving(true);
    try { const { data } = await api.put("/esl/config", { provider: cfg.provider, store_id: cfg.store_id, api_key: cfg.api_key || "", auto_push: cfg.auto_push }); setCfg({ ...data, api_key: "" }); toast.success("ESL bağlantısı kaydedildi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setCfgSaving(false); }
  };
  const testCfg = async () => {
    try { const { data } = await api.post("/esl/config/test"); toast.success(data.message); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const pushOutOfSync = async () => {
    setBusy("oos");
    try { const { data } = await api.post("/esl/push-outofsync", null, { params: locFilter ? { location_id: locFilter } : {} }); toast.success(`${data.pushed} güncel olmayan etiket gönderildi`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5 fade-in" data-testid="esl-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Katalog & Fiyat</p><h1 className="font-heading text-2xl font-bold text-slate-900">Raf Etiketleri (ESL)</h1></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="esl-loc-filter" value={locFilter} onChange={(e) => setLocFilter(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
            <option value="">Tüm lokasyonlar</option>
            {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          {canManage && <Button data-testid="esl-push-oos-btn" onClick={pushOutOfSync} disabled={busy === "oos"} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50">{busy === "oos" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Güncel Olmayanları Gönder</Button>}
          {canManage && <Button data-testid="esl-push-all-btn" onClick={pushAll} disabled={busy === "all" || summary.active === 0} className="bg-brand-orange text-white hover:bg-orange-600">{busy === "all" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Tümüne Fiyat Gönder</Button>}
          {canManage && <Button data-testid="esl-add-btn" onClick={() => setOpen(true)} className="bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Etiket</Button>}
        </div>
      </div>

      <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 ring-1 ring-inset ring-amber-200" data-testid="esl-mock-note">
        <b>Simülasyon:</b> Fiyat gönderimi ESL donanım sağlayıcısı (SES-imagotag / Pricer / Hanshow) API'si eklendiğinde gerçek cihazlara iletilecek. Altyapı, kart fiyatı değiştikçe "güncel değil" durumunu otomatik izler.
      </div>

      {canManage && cfg && (
        <div className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="esl-config-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-800">Sağlayıcı Bağlantısı {cfg.connected && <span className="ml-1 text-[10px] font-semibold text-emerald-600">● bağlı</span>}</p>
            <div className="flex gap-2">
              <Button data-testid="esl-cfg-test" size="sm" variant="outline" onClick={testCfg} disabled={!cfg.provider || (!cfg.api_key && !cfg.api_key_set)}>Bağlantıyı Test Et</Button>
              <Button data-testid="esl-cfg-save" size="sm" onClick={saveCfg} disabled={cfgSaving} className="bg-brand-green text-white hover:bg-brand-green-600">{cfgSaving ? "..." : "Kaydet"}</Button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Sağlayıcı</label>
              <select data-testid="esl-cfg-provider" value={cfg.provider || ""} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Seçiniz</option>
                {["SES-imagotag", "Pricer", "Hanshow", "Diğer"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div><label className="mb-1 block text-xs text-slate-500">Mağaza / Store ID</label><Input data-testid="esl-cfg-store" value={cfg.store_id || ""} onChange={(e) => setCfg({ ...cfg, store_id: e.target.value })} className="h-9" /></div>
            <div><label className="mb-1 block text-xs text-slate-500">API Anahtarı {cfg.api_key_set && <span className="text-[10px] text-emerald-600">(kayıtlı)</span>}</label><Input data-testid="esl-cfg-key" type="password" autoComplete="new-password" value={cfg.api_key || ""} onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })} placeholder={cfg.api_key_set ? "•••••• (değiştirmek için yazın)" : "API key"} className="h-9" /></div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600"><input data-testid="esl-cfg-autopush" type="checkbox" checked={!!cfg.auto_push} onChange={(e) => setCfg({ ...cfg, auto_push: e.target.checked })} className="h-4 w-4 rounded border-slate-300" /> Fiyat değişince otomatik gönder (auto-push)</label>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat testid="esl-stat-total" label="Toplam Etiket" value={summary.total || 0} />
        <Stat testid="esl-stat-active" label="Aktif" value={summary.active || 0} cls="text-brand-green" />
        <Stat testid="esl-stat-insync" label="Güncel" value={summary.in_sync || 0} cls="text-emerald-600" />
        <Stat testid="esl-stat-outsync" label="Güncel Değil" value={summary.out_of_sync || 0} cls="text-amber-600" />
        <Stat testid="esl-stat-never" label="Hiç Gönderilmedi" value={summary.never_pushed || 0} cls="text-slate-400" />
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input data-testid="esl-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Etiket kodu / ürün / barkod ara..." className="pl-8" />
      </div>

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3">Etiket Kodu</th>
              <th className="px-4 py-3">Ürün</th>
              <th className="px-4 py-3">Lokasyon</th>
              <th className="px-4 py-3 text-right">Güncel Fiyat</th>
              <th className="px-4 py-3 text-right">Gönderilen</th>
              <th className="px-4 py-3">Durum</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && labels.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Kayıtlı raf etiketi yok. "Yeni Etiket" ile bir ürünü bir etiket koduna bağlayın.</td></tr>}
            {labels.map((l) => (
              <tr key={l.id} data-testid={`esl-row-${l.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{l.label_code}</td>
                <td className="px-4 py-3"><p className="font-medium text-slate-700">{l.product_name}</p>{l.barcode && <p className="text-[11px] text-slate-400">{l.barcode}</p>}</td>
                <td className="px-4 py-3 text-slate-500">{l.location_name || <span className="text-slate-400">Genel</span>}</td>
                <td className="px-4 py-3 text-right tabular font-bold text-slate-800">{fmtTL(l.current_price)}</td>
                <td className="px-4 py-3 text-right tabular text-slate-500">{l.last_pushed_price != null ? fmtTL(l.last_pushed_price) : "—"}</td>
                <td className="px-4 py-3">
                  {l.status === "inactive" ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Pasif</span>
                    : l.last_pushed_price == null ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Bekliyor</span>
                    : l.in_sync ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Güncel</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> Güncel değil</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`esl-push-${l.id}`} disabled={busy === l.id || l.status === "inactive"} onClick={() => pushOne(l)} className="inline-flex items-center gap-1 rounded-md border border-brand-orange px-2 py-1 text-[11px] font-semibold text-brand-orange hover:bg-orange-50 disabled:opacity-40">{busy === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Gönder</button>
                      <button data-testid={`esl-toggle-${l.id}`} onClick={() => toggleStatus(l)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-700">{l.status === "active" ? "Pasifle" : "Aktifle"}</button>
                      <button data-testid={`esl-del-${l.id}`} onClick={() => del(l)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <EslLabelDialog locs={locs} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function EslLabelDialog({ locs, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [locId, setLocId] = useState("");
  const [prodQ, setProdQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (picked) return;
    const t = setTimeout(() => {
      if (prodQ.trim().length < 2) { setResults([]); return; }
      api.get("/esl/product-search", { params: { q: prodQ } }).then((r) => setResults(r.data || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [prodQ, picked]);

  const save = async () => {
    if (!code.trim()) { toast.error("Etiket kodu zorunlu"); return; }
    if (!picked) { toast.error("Bir ürün seçin"); return; }
    setBusy(true);
    try {
      await api.post("/esl/labels", { label_code: code.trim(), product_id: picked.id, location_id: locId || null });
      toast.success("Etiket eklendi"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="esl-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni Raf Etiketi</DialogTitle>
          <DialogDescription>Bir ESL etiket kodunu bir ürüne bağlayın. Ürün fiyatı değiştikçe etiket "güncel değil" olarak işaretlenir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs text-slate-500">Etiket Kodu *</label><Input data-testid="esl-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Örn. ESL-000123" /></div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Ürün *</label>
            {picked ? (
              <div className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200">
                <span className="text-sm font-medium text-slate-700">{picked.name} <span className="text-[11px] text-slate-400">{picked.barcode}</span></span>
                <button data-testid="esl-prod-clear" onClick={() => { setPicked(null); setProdQ(""); }} className="text-xs font-semibold text-red-500">Değiştir</button>
              </div>
            ) : (
              <>
                <Input data-testid="esl-prod-search" value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Ürün adı veya barkod ara..." />
                {results.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200">
                    {results.map((p) => (
                      <button key={p.id} data-testid={`esl-prod-opt-${p.id}`} onClick={() => setPicked(p)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50">
                        <span>{p.name} <span className="text-[11px] text-slate-400">{p.barcode}</span></span>
                        <span className="tabular text-xs font-semibold text-slate-600">{fmtTL(p.price)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Lokasyon (opsiyonel)</label>
            <select data-testid="esl-loc" value={locId} onChange={(e) => setLocId(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">Genel / Atanmamış</option>
              {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <Button data-testid="esl-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor..." : "Etiketi Kaydet"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
